import type { ReactNode } from "react";
import { cn } from "@core/utils";

type BadgeTone = "neutral" | "success" | "warning" | "error";

interface BadgeProps {
  tone?: BadgeTone;
  /** Outlined chip on a transparent ground, for identifiers such as a cohort code (« COH-2026 »). */
  outlined?: boolean;
  /** Leading status dot. */
  dot?: boolean;
  children: ReactNode;
  className?: string;
}

/**
 * Small status chip. It knows no domain: each module maps its own statuses to a tone
 * (Envoyée to success, Renvoyée to warning, and so on), so adding a status never touches core.
 *
 * Tones follow the status mapping Raouf approved (success = menthe, warning = gold,
 * error = error, neutral = ink) and read the semantic tokens, not raw colours.
 */
const SOFT: Record<BadgeTone, string> = {
  neutral:
    "bg-(--status-draft-bg) border-(--status-draft-border) text-(--status-draft-text)",
  success:
    "bg-(--status-open-bg) border-(--status-open-border) text-(--status-open-text)",
  warning:
    "bg-(--status-closing-soon-bg) border-transparent text-(--status-closing-soon-text)",
  error:
    "bg-(--feedback-error-ring-outer) border-(--feedback-error-ring) text-(--text-error)",
};

const DOT: Record<BadgeTone, string> = {
  neutral: "bg-(--status-draft-dot)",
  success: "bg-(--status-open-dot)",
  warning: "bg-(--status-closing-soon-dot)",
  error: "bg-(--feedback-error-icon)",
};

export function Badge({
  tone = "neutral",
  outlined = false,
  dot = false,
  children,
  className,
}: BadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-xxs rounded-full border px-2 py-0.5 text-xs-medium whitespace-nowrap",
        outlined
          ? "border-(--border-field) bg-transparent text-(--text-secondary)"
          : SOFT[tone],
        className
      )}
    >
      {dot ? (
        <span aria-hidden className={cn("size-1.5 rounded-full", DOT[tone])} />
      ) : null}
      {children}
    </span>
  );
}

export type { BadgeProps, BadgeTone };
